// Story 14.4 (ADO-1090) — Admin import-monitoring read client (AC2).
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// `GET /admin/import-monitoring[/:runId]` endpoints. Mirrors `audit-log.client.ts`
// exactly: the SINGLE consumer seam until the `openapi-typescript`-generated
// client lands (Story 6.1 deviation D-1). The read is scoped + role-gated
// server-side (a SystemAdministrator reads every tenant's runs, other admins
// their own tenant's); this client only shapes the request and guards the
// response envelope. Error-log pagination follows the server's `seq desc` (insertion)
// order: pass the previous page's opaque `nextCursor` to fetch the next page.

import { API_PREFIX, apiFetch } from './shoplink-api';

export const IMPORT_MONITORING_PATH = `${API_PREFIX}/admin/import-monitoring`;

/** One import run (AC1 fields + derived counts). Dates are ISO-8601 UTC strings. */
export interface ImportRun {
  readonly id: string;
  /** The tenant the run was recorded under (a SystemAdministrator sees every tenant's runs). */
  readonly tenantId: string;
  readonly feedType: string;
  /** null ⇒ a tenant-wide feed (catalogue/supplier); a UUID ⇒ a per-depot feed. */
  readonly depotId: string | null;
  readonly jobStatus: string;
  readonly validationStatus: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly processedRowCount: number;
  readonly failedRowCount: number;
  readonly appliedRowCount: number;
  readonly errorCount: number;
  readonly failureRate: number;
  /** The live AC3 failure-rate threshold the alarm fires on; drives the hot-run tint. */
  readonly failureRateThreshold: number;
  readonly createdAt: string;
}

/** One recorded error/warning on a run. */
export interface ImportRunError {
  readonly id: string;
  readonly errorType: string;
  readonly severity: string;
  readonly rowNumber: number | null;
  readonly fieldName: string | null;
  readonly detail: string | null;
  readonly createdAt: string;
}

/** The opaque exclusive next-page cursor for a run's error log (a `seq` string). */
export interface ImportErrorCursor {
  readonly seq: string;
}

/** A run's detail: the run, its source file, and a page of its error log. */
export interface ImportRunDetail {
  readonly run: ImportRun;
  readonly file: { readonly s3Bucket: string; readonly s3Key: string; readonly s3Etag: string };
  readonly errors: {
    readonly entries: ImportRunError[];
    readonly nextCursor: ImportErrorCursor | null;
  };
}

/**
 * List the latest run per pipeline the caller may see. Throws on a non-2xx so the UI
 * can surface a load-error banner. Guards the response: a 2xx whose body is not
 * an array of well-formed runs (contract drift / proxy HTML) degrades to the
 * error banner rather than white-screening the table.
 */
export async function listImportRuns(): Promise<ImportRun[]> {
  const res = await apiFetch(IMPORT_MONITORING_PATH);
  if (!res.ok) throw new Error(`Failed to load import monitoring (${res.status})`);
  const data = (await res.json()) as unknown;
  if (!Array.isArray(data) || !data.every(isImportRun)) {
    throw new Error('Unexpected import-monitoring response shape');
  }
  return data;
}

/**
 * Read one run's detail + a page of its error log. `cursor` (a previous page's
 * `nextCursor`) pages the error log. Throws on a non-2xx (incl. 404 for an
 * unknown run) so the UI can surface an error.
 */
export async function getImportRun(
  runId: string,
  cursor?: ImportErrorCursor | null,
): Promise<ImportRunDetail> {
  const params = new URLSearchParams();
  if (cursor != null) {
    params.set('beforeSeq', cursor.seq);
  }
  const qs = params.toString();
  const url = `${IMPORT_MONITORING_PATH}/${encodeURIComponent(runId)}${qs === '' ? '' : `?${qs}`}`;
  const res = await apiFetch(url);
  if (!res.ok) throw new Error(`Failed to load import run (${res.status})`);
  const data = (await res.json()) as unknown;
  if (!isImportRunDetail(data)) throw new Error('Unexpected import-run response shape');
  return data;
}

/** Run-shape guard — every wire field present and correctly typed. */
function isImportRun(value: unknown): value is ImportRun {
  if (typeof value !== 'object' || value === null) return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.tenantId === 'string' &&
    typeof r.feedType === 'string' &&
    (r.depotId === null || typeof r.depotId === 'string') &&
    typeof r.jobStatus === 'string' &&
    typeof r.validationStatus === 'string' &&
    (r.startedAt === null || typeof r.startedAt === 'string') &&
    (r.completedAt === null || typeof r.completedAt === 'string') &&
    typeof r.processedRowCount === 'number' &&
    typeof r.failedRowCount === 'number' &&
    typeof r.appliedRowCount === 'number' &&
    typeof r.errorCount === 'number' &&
    typeof r.failureRate === 'number' &&
    typeof r.failureRateThreshold === 'number' &&
    typeof r.createdAt === 'string'
  );
}

/** Error-entry-shape guard. */
function isImportRunError(value: unknown): value is ImportRunError {
  if (typeof value !== 'object' || value === null) return false;
  const e = value as Record<string, unknown>;
  return (
    typeof e.id === 'string' &&
    typeof e.errorType === 'string' &&
    typeof e.severity === 'string' &&
    (e.rowNumber === null || typeof e.rowNumber === 'number') &&
    (e.fieldName === null || typeof e.fieldName === 'string') &&
    (e.detail === null || typeof e.detail === 'string') &&
    typeof e.createdAt === 'string'
  );
}

/** Detail-envelope guard — `{ run, file, errors: { entries, nextCursor } }`. */
function isImportRunDetail(value: unknown): value is ImportRunDetail {
  if (typeof value !== 'object' || value === null) return false;
  const d = value as { run?: unknown; file?: unknown; errors?: unknown };
  if (!isImportRun(d.run)) return false;
  if (typeof d.file !== 'object' || d.file === null) return false;
  const f = d.file as Record<string, unknown>;
  if (
    typeof f.s3Bucket !== 'string' ||
    typeof f.s3Key !== 'string' ||
    typeof f.s3Etag !== 'string'
  ) {
    return false;
  }
  if (typeof d.errors !== 'object' || d.errors === null) return false;
  const errs = d.errors as { entries?: unknown; nextCursor?: unknown };
  if (!Array.isArray(errs.entries) || !errs.entries.every(isImportRunError)) return false;
  const cursor = errs.nextCursor;
  if (cursor === null || cursor === undefined) return true;
  if (typeof cursor !== 'object') return false;
  const c = cursor as { seq?: unknown };
  return typeof c.seq === 'string';
}
